import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  LayoutDashboard, Calendar, Home, Users, Settings, LogOut, 
  Menu, X, ChevronRight, Globe
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/1tzx91jj_504423129_17847547452489584_2138028376177138441_n.jpg";

const AdminLayout = () => {
  const { t, language, setLanguage } = useLanguage();
  const { user, logout, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, loading, navigate]);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2D5016]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: t('admin_dashboard') },
    { path: '/admin/reservations', icon: Calendar, label: t('admin_reservations') },
    { path: '/admin/cottages', icon: Home, label: t('admin_cottages') },
    { path: '/admin/customers', icon: Users, label: t('admin_customers') },
    { path: '/admin/settings', icon: Settings, label: t('admin_settings') },
  ];

  const languages = [
    { code: 'az', label: 'AZ' },
    { code: 'en', label: 'EN' },
    { code: 'ru', label: 'RU' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex" data-testid="admin-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 admin-sidebar transform transition-transform duration-300 lg:relative lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo & Close button */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Oriva Quba" className="h-10 w-10 rounded-lg" />
              <div>
                <h2 className="text-white font-semibold">Oriva Quba</h2>
                <p className="text-white/50 text-xs">Admin Panel</p>
              </div>
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 text-white/70 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4">
            <ul className="space-y-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive 
                          ? 'bg-[#D97706] text-white' 
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                      data-testid={`nav-${item.path.split('/').pop()}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-white/10">
            {user && (
              <div className="mb-4 px-4">
                <p className="text-white text-sm font-medium">{user.name}</p>
                <p className="text-white/50 text-xs">{user.email}</p>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-white/70 hover:bg-white/10 hover:text-white rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm font-medium">{t('admin_logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-white border-b border-[#E5E3DB] px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-[#F9F8F6] rounded-lg transition-colors lg:hidden"
              data-testid="toggle-sidebar-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-[#4A5D43]">
              <span className="hidden sm:inline">
                <Link to="/admin/dashboard" className="hover:text-[#2D5016]">Admin</Link>
                <ChevronRight className="w-4 h-4 inline mx-1" />
              </span>
              <span className="text-[#1A2415] font-medium">
                {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F9F8F6] transition-colors"
                data-testid="admin-language-switcher"
              >
                <Globe className="w-4 h-4 text-[#4A5D43]" />
                <span className="text-sm font-medium text-[#1A2415]">{language.toUpperCase()}</span>
              </button>
              
              {langMenuOpen && (
                <div className="absolute right-0 mt-2 w-24 bg-white rounded-lg shadow-lg py-1 z-50 border border-[#E5E3DB]">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setLangMenuOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-[#F9F8F6] ${
                        language === lang.code ? 'text-[#D97706] font-medium' : 'text-[#1A2415]'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Back to site */}
            <a 
              href="/" 
              className="text-sm text-[#4A5D43] hover:text-[#2D5016] hidden sm:block"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('nav_home')} &rarr;
            </a>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 overflow-x-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
